import React, { useRef } from 'react'
import PropTypes from 'prop-types'
import { Box, Typography } from '@mui/material'
import { useDocPreview } from 'hooks/useDocPreview'
import SidesUploadPreview from 'components/SidesUploadPreview'
import { ACCEPTED_FILE_TYPES, BACK, FRONT } from 'constants/docUploadConstant'
import ImagePreviewActions from 'components/ImagePreviewActions'
import { NEW_CONTAINER_WIDTH, NEW_RES_ACCORDIAN_WIDTH, VECHILE_CONTAINER_WIDTH } from 'constants/layoutConstant'
import MarkerIcon from 'images/MarkerIcon'
import FrontUploadIcon from 'images/FrontUpload'
import { useDocListStore } from 'store/useDocListStore'
import { MESSAGES } from '../messages/messages'

const UploadInput = ({ name, setPreview, onKeyDownHandler }) => {
    const fileRef = useRef()

    const selectFile = () => {
        fileRef.current.click()
    }

    const handleKeyDown = (e) => {
        onKeyDownHandler(e, selectFile)
    }

    return (
        <Box
            data-testid={`document_upload_driver_${name}`}
            onClick={selectFile}
            onKeyDown={handleKeyDown}
            tabIndex={0}
            sx={{
                maxWidth: { xs: '100%', smd: '380px', md: '436px' },
                width: '100%',
                border: '1px solid #0562D2',
                borderRadius: 4,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
                gap: 2.5,
                py: { xs: 14.5, smd: 17, md: 19.31 },
                mb: 14
            }}
        >
            <FrontUploadIcon />
            <Typography
                data-testid={`driver_${name}_title`}
                sx={{
                    typography: 'boldBody2',
                    color: 'common.fordTwilight.main'
                }}
            >
                {name}
            </Typography>

            <input
                type="file"
                accept={ACCEPTED_FILE_TYPES}
                onChange={setPreview}
                onClick={(event) => {
                    event.target.value = null
                }}
                ref={fileRef}
                hidden
            />
        </Box>
    )
}

UploadInput.propTypes = {
    name: PropTypes.string,
    setPreview: PropTypes.func,
    onKeyDownHandler: PropTypes.func
}

const PreviewWindow = ({ base64Data, mimeType, name, handleDelete, handleEdit }) => {
    const { setLoadingProgress, setIsLoading } = useDocPreview()

    return (
        <Box
            data-testid={`document_upload_driver_${name}`}
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 4,
                width: { xs: '100%', smd: '380px', md: '436px' }
            }}
        >
            <Box
                data-testid={`driver_img_block`}
                sx={{
                    border: '1px solid #0562D2',
                    borderRadius: 4,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    gap: 2.5,
                    py: 3
                }}
            >
                <Typography
                    sx={{
                        color: 'common.gray.6',
                        typography: 'regularBody3'
                    }}
                >
                    {name}
                </Typography>
                <Box>
                    <SidesUploadPreview
                        base64Data={base64Data}
                        mimeType={mimeType}
                        setLoadingProgress={setLoadingProgress}
                        setIsLoading={setIsLoading}
                    />
                </Box>
            </Box>
            <ImagePreviewActions mimeType={mimeType} handleEdit={handleEdit} handleDelete={handleDelete} />
        </Box>
    )
}

PreviewWindow.propTypes = {
    base64Data: PropTypes.string,
    name: PropTypes.string,
    mimeType: PropTypes.string,
    handleDelete: PropTypes.func,
    handleEdit: PropTypes.func
}

const SidesUpload = () => {
    const { dealerName } = useDocListStore((state) => state)

    const {
        preview,
        backSidePreview,
        setPreview,
        setBackSidePreview,
        handleSidesPreview,
        onClearPreview,
        onClearBackSidePreview,
        openEditPreview,
        onKeyDownHandler
    } = useDocPreview()

    const previewItems = [
        {
            name: FRONT,
            ...preview,
            setPreview: (event) => handleSidesPreview(event, preview, setPreview),
            handleDelete: onClearPreview,
            handleEdit: () => {
                openEditPreview(preview, false)
            }
        },
        {
            name: BACK,
            ...backSidePreview,
            setPreview: (event) => handleSidesPreview(event, backSidePreview, setBackSidePreview),
            handleDelete: onClearBackSidePreview,
            handleEdit: () => {
                openEditPreview(backSidePreview, true)
            }
        }
    ]

    return (
        <Box
            sx={{
                backgroundColor: 'common.white.main',
                px: { xs: 4, md: 4 }
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: { xs: 6, smd: 12, md: 22 },
                    px: { xs: 0, md: 10 },
                    maxWidth: NEW_CONTAINER_WIDTH,
                    margin: 'auto',
                    width: '100%'
                }}
            >
                <Typography
                    data-testid="document_upload_title"
                    sx={{
                        color: 'common.charcoal.main',
                        typography: { xs: 'smallHeadline4', md: 'smallHeadline0' },
                        fontWeight: 600
                    }}
                >
                    {MESSAGES.document_upload}
                </Typography>

                <Box
                    sx={{
                        maxWidth: {
                            xs: 'initial',
                            smd: NEW_RES_ACCORDIAN_WIDTH,
                            md: VECHILE_CONTAINER_WIDTH
                        },
                        margin: 'auto',
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 14
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 4,
                            width: '100%'
                        }}
                    >
                        <Box
                            data-testid="document_upload_driver_title"
                            sx={{
                                display: 'flex',
                                flexDirection: { xs: 'column', smd: 'row' },
                                gap: { xs: 1, smd: 1.5 }
                            }}
                        >
                            <Typography
                                sx={{
                                    color: 'common.charcoal.main',
                                    typography: { xs: 'regularSubTitle', md: 'smallHeadline3' }
                                }}
                            >
                                {MESSAGES.upload_driver_license}
                            </Typography>
                            <Typography
                                sx={{
                                    color: 'common.charcoal.main',
                                    typography: { xs: 'regularSubTitle', md: 'smallHeadline3' }
                                }}
                            >
                                ({MESSAGES.coapplicant})
                            </Typography>
                        </Box>

                        <Box
                            sx={{
                                display: 'flex',
                                gap: 1,
                                flexWrap: 'wrap',
                                columnGap: 4
                            }}
                        >
                            <Typography
                                data-testid="documents_requested_by"
                                component={'span'}
                                sx={{
                                    color: 'common.charcoal.main',
                                    typography: 'boldBody2'
                                }}
                            >
                                {MESSAGES.documents_requested_by}
                            </Typography>
                            <Box
                                sx={{
                                    display: 'flex',
                                    gap: 1
                                }}
                            >
                                <Box data-testid="location_Icon">
                                    <MarkerIcon />
                                </Box>
                                <Box>
                                    <Typography
                                        component={'span'}
                                        data-testid="dealer-name"
                                        sx={{
                                            color: 'common.charcoal.main',
                                            typography: 'regularBody2'
                                        }}
                                    >
                                        {dealerName}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                    <Box>
                        <Box
                            sx={{
                                pb: { xs: 9, md: 4 },
                                px: { xs: 0, md: 0 },
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                flexDirection: { xs: 'column', md: 'row' },
                                gap: { xs: 9, md: 4 }
                            }}
                        >
                            {previewItems.map((item) =>
                                item?.base64Data ? (
                                    <PreviewWindow key={item.name} {...item} />
                                ) : (
                                    <UploadInput key={item.name} onKeyDownHandler={onKeyDownHandler} {...item} />
                                )
                            )}
                        </Box>

                        <Typography
                            data-testid="max_file_size_and_types_warning"
                            sx={{
                                color: 'common.charcoal.main',
                                typography: 'regularBody2',
                                textAlign: 'center',
                                pb: { xs: 10, smd: 14 }
                            }}
                        >
                            {MESSAGES.max_file_size_and_types}
                        </Typography>
                    </Box>
                </Box>
            </Box>
        </Box>
    )
}

export default SidesUpload
